/*
 * Copyright (c) 2024 SARL SIGMA POLYNESIA
 */

import { SetStateAction, useCallback, useMemo, useState } from 'react'
import usePaginationQuery from '../../../../client/js/components/pagination/usePaginationQuery'
import { useQuery } from '@tanstack/react-query'
import { deleteIntervention, findManyInterventions } from '../../lib/rest/interventionAPIClient'
import { getHalEmbedded, getHalPageTotalElements } from '@jalik/hal'
import { QueryParams } from '../../../../client/js/hooks/useQueryParams'
import useSelection from '../../../../client/js/components/selection/useSelection'
import InterventionListAlerts from './InterventionListAlerts'
import SelectionAlert from '../../../../client/js/components/selection/SelectionAlert'
import LoadingHelper from '../../../../client/js/components/LoadingHelper'
import InterventionList from './InterventionList'
import DeleteSelectionModal from '../../../../client/js/components/modals/DeleteSelectionModal'
import ListCard from '../../../../client/js/components/ListCard'
import RequirePerm from '../../../auth/components/RequirePerm'
import { FormattedMessage } from 'react-intl'
import SearchForm from '../../../../client/js/components/search/SearchForm'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSyncAlt } from '@fortawesome/free-solid-svg-icons/faSyncAlt'
import { faFilter } from '@fortawesome/free-solid-svg-icons/faFilter'
import { faTrash } from '@fortawesome/free-solid-svg-icons/faTrash'
import Filters from '../../../../client/js/components/Filters'
import InterventionStatusFilter from '../filters/InterventionStatusFilter'
import ExpiredFilter from '../filters/ExpiredFilter'
import MissingTransactionFilter from '../filters/MissingTransactionFilter'
import { Button } from '@mantine/core'
import AnchorLink from '../../../../client/js/components/AnchorLink'
import Toolbar from '../../../../client/js/components/Toolbar'
import { parseNumber } from '@jalik/form-parser'
import { getInterventionCreateLink } from '../../lib/links/interventionLinks'

export type InterventionListQuery = {
  limit?: string,
  page?: string,
  search?: string,
  status?: string[],
  expired?: string,
  missingTransaction?: string,
  estateId?: string,
  leaseId?: string,
}

export type InterventionListCardProps = {
  createLink?: string;
  query: InterventionListQuery;
  onQueryChange (query: SetStateAction<InterventionListQuery>): void;
}

const initialQuery: InterventionListQuery = {
  limit: '10',
  page: '1'
}

function InterventionListCard (props: InterventionListCardProps) {
  const {
    createLink = getInterventionCreateLink(),
    query,
    onQueryChange
  } = props

  const [totalElements, setTotalElements] = useState<number>(0)
  const pagination = usePaginationQuery(totalElements)
  const { page, limit } = pagination

  const queryParams = useMemo(() => {
    return {
      ...initialQuery,
      ...query,
      page,
      limit
    }
  }, [limit, page, query])

  const listQuery = useQuery({
    enabled: true,
    queryKey: ['interventions', queryParams],
    queryFn: () => findManyInterventions(queryParams).then((r) => {
      setTotalElements(getHalPageTotalElements(r))
      return r
    })
  })

  const items = useMemo(() => (
    (listQuery.data != null ? getHalEmbedded(listQuery.data)?.interventions : null) || []
  ), [listQuery.data])

  const setFilter = useCallback((name: string, value?: unknown) => {
    onQueryChange((s: QueryParams) => ({ ...s, page: '1', [name]: value }))
  }, [onQueryChange])

  const setSearch = useCallback((search?: string) => {
    setFilter('search', search)
  }, [setFilter])

  // Items selection.
  const [selectedItems, setSelectedItems] = useState<number[]>([])
  const selection = useSelection<number>({
    multiple: true,
    items: selectedItems,
    onChange: setSelectedItems,
    source: items.map((el) => el.id)
  })

  // Items deletion.
  const handleDelete = useCallback((id: number) => deleteIntervention(id), [])
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
  const toggleDeleteModal = useCallback(() => {
    setIsDeleteModalOpen((s) => !s)
  }, [])

  // Items filtering.
  const [isFiltersVisible, setIsFiltersVisible] = useState(true)
  const toggleFilters = useCallback(() => {
    setIsFiltersVisible((s) => !s)
  }, [])

  return (
    <ListCard
      filters={<Filters in={isFiltersVisible}>
        <InterventionStatusFilter
          onChange={setFilter}
          value={queryParams.status}
        />
        <div>
          <ExpiredFilter
            onChange={setFilter}
            value={queryParams.expired}
          />
          <MissingTransactionFilter
            onChange={setFilter}
            value={queryParams.missingTransaction}
          />
        </div>
      </Filters>}
      pagination={pagination}
      selection={selection}
      selectionActions={<SelectionAlert>
        <RequirePerm
          permissions={['write:intervention']}
          render={() => (
            <Button onClick={toggleDeleteModal}>
              <FontAwesomeIcon
                fixedWidth
                icon={faTrash}
              />
            </Button>
          )}
        />
      </SelectionAlert>}
      toolbar={<Toolbar>
        <RequirePerm
          permissions={['write:intervention']}
          render={() => (
            <Button
              variant="filled"
              component={AnchorLink}
              to={createLink}
            >
              <FormattedMessage
                id="create_intervention"
                defaultMessage="Créer une intervention"
              />
            </Button>
          )}
        />
        <SearchForm
          initialValue={queryParams.search}
          onSubmit={setSearch}
          submitting={listQuery.isFetching}
        />
        <Button
          loading={listQuery.isFetching}
          onClick={() => {
            listQuery.refetch()
          }}
        >
          <FontAwesomeIcon
            fixedWidth
            icon={faSyncAlt}
          />
        </Button>
        <Button
          // todo active={isFiltersVisible}
          onClick={toggleFilters}
        >
          <FontAwesomeIcon
            fixedWidth
            icon={faFilter}
          />
        </Button>
      </Toolbar>}
    >
      <InterventionListAlerts
        estateId={parseNumber(query.estateId)}
        leaseId={parseNumber(query.leaseId)}
      />

      <LoadingHelper
        query={listQuery}
        render={() => (
          <InterventionList items={items} />
        )}
      />

      {isDeleteModalOpen
        ? <DeleteSelectionModal
          opened={isDeleteModalOpen}
          items={selection.items}
          onConfirm={handleDelete}
          onSuccess={() => {
            selection.clear()
            listQuery.refetch()
          }}
          onClose={toggleDeleteModal}
        />
        : null}
    </ListCard>
  )
}

export default InterventionListCard
